import React from 'react';
import DraftBanner from '../components/DraftBanner';
import LegalLayout from '../components/LegalLayout';

export default function Privacy() {
  return (
    <>
      <DraftBanner documentName="Privacy Policy" />

      <LegalLayout
        eyebrow="Privacy Policy"
        title="What we collect, what we do with it."
        lastUpdated="Draft — pre-launch"
        intro="OfficerCapital is a private marketplace. Most of what we know about you, you told us — and most of how we use it is in service of putting you in front of the right capital partner. This document describes that, in plain English."
        sections={[
          {
            id: 'overview',
            title: 'Overview',
            body: (
              <>
                <p>
                  OfficerCapital ("we", "us", "the platform") is operated by OfficerCapital, Inc. We collect information
                  from two groups of users: <strong>guard company owners</strong> who list their companies, and{' '}
                  <strong>capital partners</strong> (lenders, investors, acquirers) who apply for access to listings.
                </p>
                <p>
                  This policy applies to all of officercapital.com and the connected web application. It does not apply
                  to OfficerApps, which is operated by an affiliated company under a separate privacy policy.
                </p>
              </>
            ),
          },
          {
            id: 'collected',
            title: 'Information we collect',
            body: (
              <>
                <h3>Account information</h3>
                <p>
                  When you create an account we collect your name, email address, role (owner or capital partner),
                  password (stored hashed, never in plain text), and — if you sign in with Google — the basic profile
                  information Google shares with us.
                </p>

                <h3>Listing information (owners only)</h3>
                <p>
                  When you build a listing we collect what you put in it: financial figures, customer mix categories,
                  geographic information, the path you're pursuing (financing, investors, sale), and any documents you
                  attach. We do not ask for and do not store your company's legal name on the public listing record.
                </p>

                <h3>Application information (capital partners only)</h3>
                <p>
                  When you apply for partner access we collect your firm details, fund size or AUM, prior deal history,
                  and references. References are not contacted without your acknowledgment.
                </p>

                <h3>Activity information</h3>
                <p>
                  We log access events — who unlocked which listing, when, from what IP address — to maintain the audit
                  trail that owners rely on. We log sign-in events for security. We log subscription and unlock payments
                  for billing.
                </p>

                <h3>Messages</h3>
                <p>
                  Direct messages between owners and approved capital partners are stored on our servers. We do not read
                  them in the ordinary course of operating the platform; we may access them only in narrow circumstances
                  described under "How we use it" below.
                </p>

                <h3>Payment information</h3>
                <p>
                  Payments are processed by a third-party payment provider. We do not store full credit card numbers,
                  CVV codes, or bank account details on our servers; we store a tokenized reference that allows us to
                  charge subscription fees and unlock fees through the provider.
                </p>

                <h3>Cookies and similar technologies</h3>
                <p>
                  We use cookies for authentication (keeping you logged in) and basic site analytics. We do not use
                  third-party advertising cookies.
                </p>
              </>
            ),
          },
          {
            id: 'usage',
            title: 'How we use it',
            body: (
              <>
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Operate the marketplace — show owners' listings to vetted partners, route messages, process payments.</li>
                  <li>Enforce the access controls that owners rely on, including verifying that only approved partners see full listing files.</li>
                  <li>Maintain the audit log so that owners can see who unlocked their listing and when.</li>
                  <li>Communicate with you about your account, your listings, partner access requests, and platform updates.</li>
                  <li>Investigate suspected violations of our Terms — for example, if a partner is suspected of sharing listing files outside their firm, we may review access logs and, in serious cases, message content.</li>
                  <li>Comply with legal obligations, respond to lawful requests from authorities, and defend our legal rights.</li>
                </ul>
                <p>
                  We do <strong>not</strong> use your information to build advertising profiles, sell to third parties,
                  or train machine learning models for use outside the platform.
                </p>
              </>
            ),
          },
          {
            id: 'sharing',
            title: 'Who sees your information',
            body: (
              <>
                <h3>Anonymized listing data</h3>
                <p>
                  Listings are visible to vetted, subscribed capital partners on the marketplace, in their anonymized
                  form. Partners can see your headline metrics; they cannot see your company name, customer names,
                  exact location, or unlocked documents until you approve them individually.
                </p>

                <h3>Unlocked listing data</h3>
                <p>
                  When a capital partner pays the unlock fee for your listing AND you approve their access request, that
                  specific partner — and only that partner — sees the full listing including financial documents and any
                  identifying details you have shared.
                </p>

                <h3>Service providers</h3>
                <p>
                  We share information with third-party services that help us run the platform: hosting providers,
                  email delivery providers, payment processors, and error monitoring. These providers process data only
                  on our instructions and only to provide their service.
                </p>

                <h3>Legal obligations</h3>
                <p>
                  We may disclose information when we are legally required to — for example, in response to a subpoena
                  or court order — or when we reasonably believe disclosure is necessary to investigate fraud, prevent
                  imminent harm, or enforce our Terms.
                </p>

                <h3>What we do not do</h3>
                <p>
                  We do not sell your information. We do not syndicate your listing data to other deal-listing services.
                  We do not share your contact information with capital partners until you approve their access request.
                </p>
              </>
            ),
          },
          {
            id: 'retention',
            title: 'How long we keep it',
            body: (
              <>
                <p>
                  We keep account information for as long as your account is active. If you close your account, we
                  retain a minimal record (your email, account ID, and account closure date) for up to seven years for
                  tax, audit, and dispute purposes, then delete or anonymize it.
                </p>
                <p>
                  Listings that you remove are deleted from public view immediately. We retain a private record of the
                  listing and its access log for up to two years after removal, in case a dispute arises about access
                  that occurred while the listing was live.
                </p>
                <p>
                  Messages between you and an approved partner are retained for the life of your account. You can
                  request deletion of message threads through your account settings.
                </p>
              </>
            ),
          },
          {
            id: 'rights',
            title: 'Your rights',
            body: (
              <>
                <p>You can:</p>
                <ul>
                  <li><strong>See your data.</strong> Request a copy of the personal information we hold about you.</li>
                  <li><strong>Correct your data.</strong> Edit your account profile and listings any time.</li>
                  <li><strong>Delete your account.</strong> Close your account through account settings; we will delete or anonymize your data subject to the retention rules above.</li>
                  <li><strong>Pause your listing.</strong> Pause or remove a listing in one click. Partners with prior access lose visibility immediately.</li>
                  <li><strong>Object or withdraw consent</strong> for specific uses where we rely on consent.</li>
                </ul>
                <p>
                  Residents of certain regions (including the EU, UK, California, and others) may have additional rights
                  under local law, including the right to data portability and the right to lodge a complaint with a
                  supervisory authority. To exercise any right, email <a href="mailto:privacy@officercapital.com" style={{ color: 'var(--oc-green-deep)' }}>privacy@officercapital.com</a>.
                </p>
              </>
            ),
          },
          {
            id: 'security',
            title: 'Security',
            body: (
              <>
                <p>
                  We use industry-standard practices to protect your information — encryption in transit, encryption at
                  rest, hashed passwords, role-based access controls, and detailed audit logs. We describe our security
                  posture in more detail on the <a href="/security" style={{ color: 'var(--oc-green-deep)' }}>Security page</a>.
                </p>
                <p>
                  No system is perfectly secure. If we ever experience a breach affecting your information, we will
                  notify affected users without undue delay, consistent with applicable law.
                </p>
              </>
            ),
          },
          {
            id: 'children',
            title: 'Children',
            body: (
              <p>
                OfficerCapital is intended for business use and is not directed at children under 18. We do not
                knowingly collect personal information from children. If you believe a child has provided us with
                personal information, contact us and we will delete it.
              </p>
            ),
          },
          {
            id: 'changes',
            title: 'Changes to this policy',
            body: (
              <p>
                We may update this policy. When we do, we will update the "Last updated" date at the top and, for
                material changes, notify users by email or platform notice before the change takes effect.
              </p>
            ),
          },
          {
            id: 'contact',
            title: 'Contact',
            body: (
              <p>
                Questions, requests, or concerns about privacy can be sent to{' '}
                <a href="mailto:privacy@officercapital.com" style={{ color: 'var(--oc-green-deep)' }}>privacy@officercapital.com</a>.
              </p>
            ),
          },
        ]}
      />
    </>
  );
}
